<template>
    <div class="pl-4" v-if="templateCopy">
        <input
            v-if="allowAttachments"
            id="emailAutomationAttachment"
            class="d-none"
            ref="emailAutomationAttachment"
            type="file"
            multiple
            name="emailAutomationAttachment"
            @change="$emit('attachmentUploadChange', $event)"
        />
        <options
            v-if="isEmailAutomationTemplate"
            :template-name="templateCopy.template_name"
            :is-public="!!templateCopy.is_public"
            :is-public-disabled="isPublicDisabled"
            :can-edit-template="canEditTemplate"
            :errors="errorsCopy"
            @updateTemplateName="templateCopy.template_name = $event"
            @updateIsPublic="templateCopy.is_public = $event"
        />
        <email
            ref="emailEdit"
            :sent-data="sentData"
            :selected-locale-code="selectedLocaleCode"
            :subject="selectedRow.subject"
            :content="selectedRow.content"
            :files="form_data ? form_data.files : []"
            :can-edit-template="canEditTemplate"
            :errors="errorsCopy"
            @deleteFile="$emit('attachmentDeleteFile', $event)"
            @updateSubject="selectedRow.subject = $event"
            @updateContent="selectedRow.content = $event"
            @selectLocaleCode="selectLocaleCode"
        />
        <Footer
            :canEditTemplate="canEditTemplate"
            :can-delete-template="canDeleteTemplate"
            :is-saving="isSaving"
            :allow-attachments="allowAttachments"
            :rich-text-editor="richTextEditor"
            @addAttachment="emailAutomationAttachment.click()"
            @cancel="cancel"
            @save="save(templateCopy)"
            @delete="deleteTemplate"
        />
    </div>
</template>

<script>
import "quill/dist/quill.core.css";
import "quill/dist/quill.snow.css";
import "quill/dist/quill.bubble.css";
import Options from "./Options";
import Email from "./Email";
import Footer from "./Footer";
import { $t } from "@/i18n";
import { computed, onMounted, ref, watch } from "@vue/composition-api";
import appStoreDialog from "@/store/modules/AppStoreDialog/appStoreDialog";
import AccountModule from "../../../store/modules/account";
import AppModule from "../../../store/modules/app";
import RightsModule from "../../../composables/use-rights";
import DialogModule from "../../../store/modules/dialog";
import { CONTENT_TEMPLATE_TYPE } from "../../../helpers/constants";

export default {
    name: "EditTemplateIndex",
    components: {
        Options,
        Email,
        Footer
    },
    props: {
        template: {
            type: Object,
            required: true
        },
        errors: {
            type: [Error, Object],
            required: true
        },
        form_data: {
            type: Object,
            default: null
        }
    },
    setup(props, ctx) {
        const { appStoreDialogGetters } = appStoreDialog()
        const { current_account } = AccountModule();
        const { openError, setErrorOptions } = DialogModule();
        const { can_edit_account } = RightsModule();
        const isSaving = ref(false);
        const templateCopy = ref(null);
        const errorsCopy = ref({});
        const emailEdit = ref(null);
        const richTextEditor = computed(() => {
            if (!emailEdit.value) {
                return
            }

            return emailEdit.value.$refs.richTextEditor
        })
        const sentData = ref(null);
        const emailAutomationAttachment = ref(null);
        const selectedLocaleCode = ref(current_account.value.locale_code)
        const selectedAccount = computed(() => {
            return appStoreDialogGetters.getSelectedCompany()
        })

        const isInvoiceTemplate = computed(() => templateCopy.value.type === CONTENT_TEMPLATE_TYPE.INVOICE)
        const isEmailAutomationTemplate = computed(() => templateCopy.value.type === CONTENT_TEMPLATE_TYPE.EMAIL_AUTOMATION)
        const allowAttachments = computed(() => isEmailAutomationTemplate.value)

        const selectedRow = computed(() => templateCopy.value.rows.find(x => x.locale_code === selectedLocaleCode.value))

        const loadTemplate = async () => {
            templateCopy.value = props.template
            if (!selectedRow.value) {
                addRow(current_account.value.locale_code)
            }
        }

        const addRow = (localeCode) => {
            templateCopy.value.rows.push({
                subject: "",
                content: "",
                locale_code: localeCode
            })
        }

        const selectLocaleCode = (localeCode) => {
            selectedLocaleCode.value = localeCode
            if (!templateCopy.value.rows.find(x => x.locale_code === selectedLocaleCode.value)) {
                addRow(localeCode)
            }
        }

        const isNewTemplate = computed(() => !props.template.id);
        const isPublicDisabled = computed(() => !can_edit_account(selectedAccount.value.id))
        const isTemplateEditable = computed(() => (
            can_edit_account(selectedAccount.value.id) && templateCopy.value.is_public) ||
            !templateCopy.value.is_public)
        const canDeleteTemplate = computed(() => !isNewTemplate.value && isTemplateEditable.value && isEmailAutomationTemplate.value)
        const canEditTemplate = computed(() =>
            (templateCopy.value.is_public && can_edit_account(selectedAccount.value.id)) ||
            (!templateCopy.value.is_public)
        )

        const getMappedErrorObject = (error) => {
            let { field } = error;
            return { [field]: error.error_message }
        }

        const getSaveData = () => {
            const rows = templateCopy.value.rows.filter(x => x.subject !== '' || x.content !== '')

            let data = {
                account_id: templateCopy.value.account_id,
                is_public: templateCopy.value.is_public,
                template_name: templateCopy.value.template_name,
                type: templateCopy.value.type,
                channel: templateCopy.value.channel,
                rows: rows,
                id: templateCopy.value.id,
                creator_id: templateCopy.value.creator_id
            }

            if (isNewTemplate.value && isEmailAutomationTemplate.value) {
                data['files'] = props.form_data.files
            }

            return data
        }

        const setError = (error) => {
            const errorObj = getMappedErrorObject(error)
            if (!errorsCopy.value[error.index]) {
                errorsCopy.value[error.index] = {}
            }

            errorsCopy.value = Object.assign({}, errorsCopy.value, errorObj);
        }

        const findGlobalErrors = errors => {
            return Array.from(errors)
                .filter(error => !error.field)
                .map(error => error.error_message)
        }

        const setGlobalErrors = errors => {
            if (findGlobalErrors(errors).length > 0) {
                setErrorOptions({
                    title: $t("Error"),
                    text: errors[0].error_message
                });
                openError();
            }
        }

        const showValidationErrors = errors => {
            for (let error of Array.from(errors)) {
                setError(error)
            }

            setGlobalErrors(errors)
        };

        const save = async () => {
            const saveData = getSaveData()
            sentData.value = saveData
            ctx.emit('save', saveData)
        }

        const cancel = async () => {
            ctx.emit('cancel')
        }

        const deleteTemplate = async () => {
            ctx.emit('delete')
        }

        onMounted(() => {
            loadTemplate()
        })

        watch(() => props.errors, () => {
            if (
                props.errors &&
                props.errors.response &&
                props.errors.response.data.error_type &&
                props.errors.response.data.error_type === "form_validation"
            ) {
                errorsCopy.value = {}
                showValidationErrors(props.errors.response.data.errors);
            }
        })

        return {
            save,
            cancel,
            selectLocaleCode,
            selectedRow,
            deleteTemplate,
            isPublicDisabled,
            canEditTemplate,
            emailEdit,
            emailAutomationAttachment,
            canDeleteTemplate,
            isSaving,
            sentData,
            templateCopy,
            isInvoiceTemplate,
            isEmailAutomationTemplate,
            allowAttachments,
            errorsCopy,
            selectedLocaleCode,
            richTextEditor
        }
    }
}
</script>

<style scoped>

</style>